/**
 * The pure half of the Narration page: editor drafts for voices, tags and tag categories, the conversions to and
 * from the API's shapes, and the same checks the Admin API makes (NarrationAdminService.Apply), so
 * most mistakes show next to their field. The server stays authoritative; its 400 message is shown
 * when something gets past these.
 */

import type {
  AdminNarrationTagCategoryDto,
  AdminNarrationTagDto,
  AdminNarrationVoiceDto,
  ElevenLabsVoiceInfo,
  SaveNarrationTagCategoryRequest,
  SaveNarrationTagRequest,
  SaveNarrationVoiceRequest,
} from '@api/types/narration'

export const narrationVoicesKey = ['admin-narration', 'voices'] as const
export const narrationTagsKey = ['admin-narration', 'tags'] as const
export const narrationTagCategoriesKey = ['admin-narration', 'tag-categories'] as const
export const elevenLabsVoicesKey = ['admin-narration', 'elevenlabs-voices'] as const

/** The narration languages the apps offer. Other BCP-47 tags can be typed. */
export const NARRATION_LANGUAGES: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'vi', label: 'Vietnamese' },
  { code: 'zh', label: 'Chinese' },
]

export function languageLabel(code: string): string {
  const base = code.split('-')[0]
  const known = NARRATION_LANGUAGES.find((l) => l.code === code) ?? NARRATION_LANGUAGES.find((l) => l.code === base)
  return known ? (known.code === code ? known.label : `${known.label} (${code})`) : code
}

// Server limits (NarrationEntities.cs).
export const VOICE_ID_MAX = 64
export const VOICE_NAME_MAX = 100
export const VOICE_LANGUAGES_MAX = 200
export const VOICE_DESCRIPTION_MAX = 500
export const VOICE_PREVIEW_URL_MAX = 1000
export const TAG_MAX = 40
export const TAG_LABEL_MAX = 60
export const TAG_DESCRIPTION_MAX = 300
export const CATEGORY_NAME_MAX = 60
export const CATEGORY_DESCRIPTION_MAX = 300

const LANGUAGE_TAG = /^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/

/**
 * One BCP-47 tag in canonical case (`pt-br` → `pt-BR`, `zh-hant` → `zh-Hant`), or null when it
 * isn't one. Mirrors NodeNarrations.NormalizeLanguage, except blank is null here (a blank chip is
 * simply dropped rather than becoming "en").
 */
export function normalizeLanguageTag(raw: string): string | null {
  const trimmed = raw.trim().replace(/_/g, '-')
  if (trimmed === '' || trimmed.length > 35 || !LANGUAGE_TAG.test(trimmed)) return null
  return trimmed
    .split('-')
    .map((part, i) => {
      if (i === 0) return part.toLowerCase()
      if (part.length === 2 && /^[A-Za-z]/.test(part)) return part.toUpperCase()
      if (part.length === 4 && /^[A-Za-z]/.test(part)) return part[0].toUpperCase() + part.slice(1).toLowerCase()
      return part.toLowerCase()
    })
    .join('-')
}

/** Normalised, de-duplicated (case-insensitively, first wins) tags, plus the entries that aren't tags. */
export function normalizeLanguages(list: readonly string[]): { languages: string[]; invalid: string[] } {
  const languages: string[] = []
  const invalid: string[] = []
  for (const raw of list) {
    if (raw.trim() === '') continue
    const tag = normalizeLanguageTag(raw)
    if (tag == null) {
      invalid.push(raw.trim())
      continue
    }
    if (!languages.some((l) => l.toLowerCase() === tag.toLowerCase())) languages.push(tag)
  }
  return { languages, invalid }
}

/**
 * An optional number typed into a box: blank → null, otherwise a finite number within
 * [min, max], or an error to show under the field.
 */
export function parseOptionalNumber(
  text: string,
  min: number,
  max: number,
): { value: number | null; error?: string } {
  const trimmed = text.trim()
  if (trimmed === '') return { value: null }
  const n = Number(trimmed)
  if (!Number.isFinite(n)) return { value: null, error: `A number from ${min} to ${max}, or leave it blank.` }
  if (n < min || n > max) return { value: null, error: `From ${min} to ${max}, or leave it blank.` }
  return { value: n }
}

export function isHttpsUrl(text: string): boolean {
  try {
    return new URL(text.trim()).protocol === 'https:'
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------------------------
// Voices
// ---------------------------------------------------------------------------------------------

/** Editor state. Numbers are held as the text typed so a half-typed "0." survives a re-render. */
export interface VoiceDraft {
  elevenLabsVoiceId: string
  displayName: string
  languages: string[]
  description: string
  previewUrl: string
  stability: string
  similarityBoost: string
  style: string
  speed: string
  isActive: boolean
  sortOrder: string
}

export type VoiceNumberField = 'stability' | 'similarityBoost' | 'style' | 'speed'

export const VOICE_NUMBER_RANGES: Record<VoiceNumberField, { min: number; max: number }> = {
  stability: { min: 0, max: 1 },
  similarityBoost: { min: 0, max: 1 },
  style: { min: 0, max: 1 },
  speed: { min: 0.7, max: 1.2 },
}

export function blankVoiceDraft(sortOrder: number): VoiceDraft {
  return {
    elevenLabsVoiceId: '',
    displayName: '',
    languages: [],
    description: '',
    previewUrl: '',
    stability: '',
    similarityBoost: '',
    style: '',
    speed: '',
    isActive: true,
    sortOrder: String(sortOrder),
  }
}

const numText = (v: number | null | undefined) => (v == null ? '' : String(v))

export function voiceDraftFromDto(voice: AdminNarrationVoiceDto): VoiceDraft {
  return {
    elevenLabsVoiceId: voice.elevenLabsVoiceId,
    displayName: voice.displayName,
    languages: [...(voice.languages ?? [])],
    description: voice.description ?? '',
    previewUrl: voice.previewUrl ?? '',
    stability: numText(voice.stability),
    similarityBoost: numText(voice.similarityBoost),
    style: numText(voice.style),
    speed: numText(voice.speed),
    isActive: voice.isActive,
    sortOrder: String(voice.sortOrder),
  }
}

/**
 * Fill the ElevenLabs-side fields from a picked or looked-up voice: id, name, description, preview
 * and, when ElevenLabs gives them, the languages (mapped onto our list) and any voice setting still
 * blank. Leaves active / sort order alone.
 */
export function applyElevenLabsVoice(draft: VoiceDraft, picked: ElevenLabsVoiceInfo): VoiceDraft {
  const description = picked.description?.trim() || describeLabels(picked.labels)
  const languages = mapElevenLabsLanguages(picked.languages ?? [])
  const s = picked.settings
  const fill = (current: string, value: number | null | undefined) => (current.trim() === '' && value != null ? String(value) : current)
  return {
    ...draft,
    elevenLabsVoiceId: picked.voiceId,
    displayName: (picked.name || draft.displayName).slice(0, VOICE_NAME_MAX),
    description: (description || draft.description).slice(0, VOICE_DESCRIPTION_MAX),
    previewUrl: picked.previewUrl && isHttpsUrl(picked.previewUrl) ? picked.previewUrl : draft.previewUrl,
    languages: languages.length > 0 ? languages : draft.languages,
    stability: fill(draft.stability, s?.stability),
    similarityBoost: fill(draft.similarityBoost, s?.similarityBoost),
    style: fill(draft.style, s?.style),
    speed: fill(draft.speed, inRange(s?.speed, VOICE_NUMBER_RANGES.speed)),
  }
}

const inRange = (v: number | null | undefined, r: { min: number; max: number }) =>
  v != null && v >= r.min && v <= r.max ? v : null

/** ElevenLabs codes that mean one of ours. */
const ELEVENLABS_LANGUAGE_ALIASES: Record<string, string> = { cmn: 'zh', yue: 'zh', spa: 'es', eng: 'en', por: 'pt' }

/**
 * ElevenLabs' language codes ("en", "en-US", "cmn") onto our narration list, in our order. Codes we
 * don't narrate in are dropped: a library voice listing every language it can attempt would
 * otherwise flood the field.
 */
export function mapElevenLabsLanguages(codes: readonly string[]): string[] {
  const bases = new Set(
    codes.map((c) => {
      const base = c.trim().toLowerCase().split(/[-_]/)[0]
      return ELEVENLABS_LANGUAGE_ALIASES[base] ?? base
    }),
  )
  return NARRATION_LANGUAGES.map((l) => l.code).filter((code) => bases.has(code))
}

/**
 * Worth an automatic lookup: ElevenLabs voice ids are 20 letters and digits. The Look up button
 * accepts anything the server would (letters, digits, '_' and '-', up to 64).
 */
export function looksLikeElevenLabsVoiceId(text: string): boolean {
  return /^[A-Za-z0-9]{20}$/.test(text.trim())
}

export function isValidElevenLabsVoiceId(text: string): boolean {
  const t = text.trim()
  return t.length > 0 && t.length <= VOICE_ID_MAX && /^[A-Za-z0-9_-]+$/.test(t)
}

/** "british · old · male · narrative story", for a voice row. */
export function voiceTraits(v: ElevenLabsVoiceInfo): string {
  const pick = (value: string | null | undefined, ...labelKeys: string[]) =>
    value ?? labelKeys.map((k) => v.labels?.[k]).find(Boolean) ?? null
  return [pick(v.accent, 'accent'), pick(v.age, 'age'), pick(v.gender, 'gender'), pick(v.useCase, 'use_case', 'use case')]
    .filter((x): x is string => Boolean(x && x.trim()))
    .map((x) => x.replace(/_/g, ' '))
    .join(' · ')
}

/** Whether ElevenLabs lists eleven_v3 for the voice (null when it says nothing about models). */
export function supportsV3(v: ElevenLabsVoiceInfo): boolean | null {
  const ids = v.modelIds ?? []
  return ids.length === 0 ? null : ids.includes('eleven_v3')
}

/** Filter choices for the picker. ElevenLabs' category values differ between the two sources. */
export const ELEVENLABS_FILTERS = {
  gender: ['male', 'female', 'neutral'],
  age: ['young', 'middle_aged', 'old'],
  accountCategory: ['premade', 'cloned', 'generated', 'professional'],
  libraryCategory: ['professional', 'high_quality', 'famous'],
  useCase: [
    'narrative_story',
    'conversational',
    'characters_animation',
    'informative_educational',
    'social_media',
    'entertainment_tv',
    'advertisement',
  ],
  language: [
    ...NARRATION_LANGUAGES,
    { code: 'it', label: 'Italian' },
    { code: 'ja', label: 'Japanese' },
    { code: 'ko', label: 'Korean' },
    { code: 'hi', label: 'Hindi' },
    { code: 'ar', label: 'Arabic' },
    { code: 'nl', label: 'Dutch' },
    { code: 'pl', label: 'Polish' },
  ],
} as const

/** "middle_aged" → "Middle aged". */
export function filterLabel(value: string): string {
  const t = value.replace(/_/g, ' ')
  return t.charAt(0).toUpperCase() + t.slice(1)
}

/** "american, young, female, narration" from ElevenLabs' labels, in a stable order. */
export function describeLabels(labels: Record<string, string> | null | undefined): string {
  if (!labels) return ''
  const order = ['accent', 'age', 'gender', 'description', 'use_case', 'use case']
  const keys = Object.keys(labels).sort((a, b) => {
    const ia = order.indexOf(a)
    const ib = order.indexOf(b)
    return (ia < 0 ? order.length : ia) - (ib < 0 ? order.length : ib) || a.localeCompare(b)
  })
  return keys
    .map((k) => labels[k]?.trim())
    .filter(Boolean)
    .join(', ')
}

const isWhole = (text: string) => text.trim() !== '' && Number.isInteger(Number(text))

export function validateVoiceDraft(draft: VoiceDraft): Record<string, string> {
  const errors: Record<string, string> = {}
  const id = draft.elevenLabsVoiceId.trim()
  if (id === '' || id.length > VOICE_ID_MAX || !/^[A-Za-z0-9_-]+$/.test(id)) {
    errors.elevenLabsVoiceId = 'Required: letters, digits, - and _ only.'
  }
  const name = draft.displayName.trim()
  if (name === '' || name.length > VOICE_NAME_MAX) errors.displayName = `Required, up to ${VOICE_NAME_MAX} characters.`

  const { languages, invalid } = normalizeLanguages(draft.languages)
  if (invalid.length > 0) {
    errors.languages = `Not a language tag: ${invalid.join(', ')}. Use tags like en or pt-BR.`
  } else if (languages.join(',').length > VOICE_LANGUAGES_MAX) {
    errors.languages = 'Too many languages.'
  }

  if (draft.description.trim().length > VOICE_DESCRIPTION_MAX) {
    errors.description = `Up to ${VOICE_DESCRIPTION_MAX} characters.`
  }
  const preview = draft.previewUrl.trim()
  if (preview !== '' && (preview.length > VOICE_PREVIEW_URL_MAX || !isHttpsUrl(preview))) {
    errors.previewUrl = 'An https link, or leave it blank.'
  }

  for (const key of Object.keys(VOICE_NUMBER_RANGES) as VoiceNumberField[]) {
    const { min, max } = VOICE_NUMBER_RANGES[key]
    const parsed = parseOptionalNumber(draft[key], min, max)
    if (parsed.error) errors[key] = parsed.error
  }

  if (!isWhole(draft.sortOrder)) errors.sortOrder = 'A whole number.'
  return errors
}

/** The save body. Call after validateVoiceDraft returned no errors. */
export function voiceRequestFromDraft(draft: VoiceDraft): SaveNarrationVoiceRequest {
  const num = (key: VoiceNumberField) =>
    parseOptionalNumber(draft[key], VOICE_NUMBER_RANGES[key].min, VOICE_NUMBER_RANGES[key].max).value
  const description = draft.description.trim()
  const preview = draft.previewUrl.trim()
  return {
    elevenLabsVoiceId: draft.elevenLabsVoiceId.trim(),
    displayName: draft.displayName.trim(),
    languages: normalizeLanguages(draft.languages).languages,
    description: description === '' ? null : description,
    previewUrl: preview === '' ? null : preview,
    stability: num('stability'),
    similarityBoost: num('similarityBoost'),
    style: num('style'),
    speed: num('speed'),
    isActive: draft.isActive,
    sortOrder: Number(draft.sortOrder),
  }
}

/** A voice's own fields as a save body, e.g. to flip isActive from the list. */
export function voiceRequestFromDto(voice: AdminNarrationVoiceDto): SaveNarrationVoiceRequest {
  return {
    elevenLabsVoiceId: voice.elevenLabsVoiceId,
    displayName: voice.displayName,
    languages: voice.languages ?? [],
    description: voice.description,
    previewUrl: voice.previewUrl,
    stability: voice.stability,
    similarityBoost: voice.similarityBoost,
    style: voice.style,
    speed: voice.speed,
    isActive: voice.isActive,
    sortOrder: voice.sortOrder,
  }
}

/** Case-insensitive match on name, id, category, description and label values. */
// ---------------------------------------------------------------------------------------------
// Tags
// ---------------------------------------------------------------------------------------------

export interface TagDraft {
  tag: string
  label: string
  description: string
  /** '' = uncategorised. */
  categoryId: string
  isActive: boolean
  sortOrder: string
}

/** What the server will store for a typed tag: brackets stripped, spaces collapsed, lower-cased. */
export function normalizeTagToken(raw: string): string {
  return raw
    .trim()
    .replace(/^\[+/, '')
    .replace(/\]+$/, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
}

export function blankTagDraft(sortOrder: number, categoryId = ''): TagDraft {
  return { tag: '', label: '', description: '', categoryId, isActive: true, sortOrder: String(sortOrder) }
}

export function tagDraftFromDto(tag: AdminNarrationTagDto): TagDraft {
  return {
    tag: tag.tag,
    label: tag.label,
    description: tag.description ?? '',
    categoryId: tag.categoryId ?? '',
    isActive: tag.isActive,
    sortOrder: String(tag.sortOrder),
  }
}

export function validateTagDraft(draft: TagDraft, existing: readonly AdminNarrationTagDto[], editingId?: string) {
  const errors: Record<string, string> = {}
  const token = normalizeTagToken(draft.tag)
  if (token === '' || token.length > TAG_MAX || /[[\]\n]/.test(token)) {
    errors.tag = `Required, up to ${TAG_MAX} characters, without brackets inside.`
  } else if (existing.some((t) => t.tag === token && t.id !== editingId)) {
    errors.tag = `[${token}] is already in the list.`
  }
  if (draft.label.trim().length > TAG_LABEL_MAX) errors.label = `Up to ${TAG_LABEL_MAX} characters.`
  if (draft.description.trim().length > TAG_DESCRIPTION_MAX) {
    errors.description = `Up to ${TAG_DESCRIPTION_MAX} characters.`
  }
  if (!isWhole(draft.sortOrder)) errors.sortOrder = 'A whole number.'
  return errors
}

export function tagRequestFromDraft(draft: TagDraft): SaveNarrationTagRequest {
  const description = draft.description.trim()
  return {
    tag: normalizeTagToken(draft.tag),
    label: draft.label.trim(),
    description: description === '' ? null : description,
    categoryId: draft.categoryId || null,
    isActive: draft.isActive,
    sortOrder: Number(draft.sortOrder),
  }
}

export function tagRequestFromDto(tag: AdminNarrationTagDto): SaveNarrationTagRequest {
  return {
    tag: tag.tag,
    label: tag.label,
    description: tag.description,
    categoryId: tag.categoryId,
    isActive: tag.isActive,
    sortOrder: tag.sortOrder,
  }
}

// ---------------------------------------------------------------------------------------------
// Tag categories
// ---------------------------------------------------------------------------------------------

/** The tags table's category filter: every tag, the uncategorised, or one category's. */
export type TagCategoryFilter = 'all' | 'none' | string

export function filterTagsByCategory(
  tags: readonly AdminNarrationTagDto[],
  filter: TagCategoryFilter,
): AdminNarrationTagDto[] {
  if (filter === 'all') return [...tags]
  if (filter === 'none') return tags.filter((t) => !t.categoryId)
  return tags.filter((t) => t.categoryId === filter)
}

/** The category's name for the tags table: "—" when none, "(deleted)" when it no longer exists. */
export function categoryName(categories: readonly AdminNarrationTagCategoryDto[], id: string | null): string {
  if (!id) return '—'
  return categories.find((c) => c.id === id)?.name ?? '(deleted)'
}

/** `ids` with `id` moved one place up (-1) or down (+1); unchanged at either end or when `id` is not there. */
export function moveId(ids: readonly string[], id: string, direction: -1 | 1): string[] {
  const out = [...ids]
  const at = out.indexOf(id)
  const to = at + direction
  if (at < 0 || to < 0 || to >= out.length) return out
  ;[out[at], out[to]] = [out[to], out[at]]
  return out
}

export interface CategoryDraft {
  name: string
  description: string
  isActive: boolean
  sortOrder: string
}

export function blankCategoryDraft(sortOrder: number): CategoryDraft {
  return { name: '', description: '', isActive: true, sortOrder: String(sortOrder) }
}

export function categoryDraftFromDto(c: AdminNarrationTagCategoryDto): CategoryDraft {
  return { name: c.name, description: c.description ?? '', isActive: c.isActive, sortOrder: String(c.sortOrder) }
}

/** The server's checks: a name (unique, any case), lengths, a whole sort order. */
export function validateCategoryDraft(
  draft: CategoryDraft,
  existing: readonly AdminNarrationTagCategoryDto[],
  editingId?: string,
) {
  const errors: Record<string, string> = {}
  const name = draft.name.trim()
  if (name === '' || name.length > CATEGORY_NAME_MAX) errors.name = `Required, up to ${CATEGORY_NAME_MAX} characters.`
  else if (existing.some((c) => c.name.toLowerCase() === name.toLowerCase() && c.id !== editingId)) {
    errors.name = `There is already a category called "${name}".`
  }
  if (draft.description.trim().length > CATEGORY_DESCRIPTION_MAX) {
    errors.description = `Up to ${CATEGORY_DESCRIPTION_MAX} characters.`
  }
  if (!isWhole(draft.sortOrder)) errors.sortOrder = 'A whole number.'
  return errors
}

export function categoryRequestFromDraft(draft: CategoryDraft): SaveNarrationTagCategoryRequest {
  const description = draft.description.trim()
  return {
    name: draft.name.trim(),
    description: description === '' ? null : description,
    isActive: draft.isActive,
    sortOrder: Number(draft.sortOrder),
  }
}

export function categoryRequestFromDto(c: AdminNarrationTagCategoryDto): SaveNarrationTagCategoryRequest {
  return { name: c.name, description: c.description, isActive: c.isActive, sortOrder: c.sortOrder }
}

/** The next free sort order after everything listed. */
export function nextSortOrder(rows: readonly { sortOrder: number }[]): number {
  return rows.reduce((max, r) => Math.max(max, r.sortOrder + 1), 0)
}

// ---------------------------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------------------------

interface ErrorShape {
  response?: { status?: number; data?: unknown }
  message?: string
}

/** The Admin API's own sentence (400 `{message}`), else model-binding errors, else the fallback. */
export function narrationErrorMessage(err: unknown, fallback: string): string {
  const data = (err as ErrorShape)?.response?.data
  if (data && typeof data === 'object') {
    const d = data as { message?: string; Message?: string; errors?: Record<string, string[]>; title?: string }
    if (d.message) return d.message
    if (d.Message) return d.Message
    const first = d.errors ? Object.values(d.errors).flat()[0] : undefined
    if (first) return first
    if (d.title) return d.title
  }
  if (typeof data === 'string' && data.trim() !== '' && data.length < 300) return data
  return err instanceof Error && err.message ? err.message : fallback
}

/**
 * Why ElevenLabs voices could not be listed or looked up. A 503 is a configuration problem the
 * admin can fix (no key, or ElevenLabs refused it), so it says where the key goes; the server's own
 * reason follows when it has one. 429 is ElevenLabs rate limiting.
 */
export function elevenLabsListError(err: unknown): { title: string; detail: string | null; notConfigured: boolean } {
  const status = (err as ErrorShape)?.response?.status
  const serverMessage = narrationErrorMessage(err, '')
  if (status === 503) {
    return {
      title:
        "ElevenLabs isn't available to the Admin API. Set ElevenLabs__ApiKey in /etc/adventurecore/admin.env and restart the Admin API.",
      detail: serverMessage && !/^Request failed/.test(serverMessage) ? serverMessage : null,
      notConfigured: true,
    }
  }
  if (status === 429) {
    return { title: 'ElevenLabs is rate limiting. Try again in a moment.', detail: null, notConfigured: false }
  }
  return {
    title: narrationErrorMessage(err, 'Could not load the ElevenLabs voices.'),
    detail: null,
    notConfigured: false,
  }
}

/** The save was refused because the ElevenLabs voice isn't in the account yet (400 code voice_not_in_account). */
export function isVoiceNotInAccountError(err: unknown): boolean {
  const data = (err as ErrorShape)?.response?.data as { code?: string; Code?: string } | undefined
  return (err as ErrorShape)?.response?.status === 400 && (data?.code ?? data?.Code) === 'voice_not_in_account'
}

export const elevenLabsVoiceKey = (voiceId: string) => ['admin-narration', 'elevenlabs-voice', voiceId] as const

/** "0.5", "—" for the ElevenLabs default. */
export function formatSetting(value: number | null | undefined): string {
  return value == null ? '—' : String(value)
}
